import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Briefcase,
  Loader2,
  Edit,
  Search,
  Plus,
  Trash2,
  AlertCircle,
  Users,
  Building2,
  FileCheck,
  Receipt,
  Calendar,
  Stethoscope,
  ArrowLeftRight,
  Shield,
  HardHat,
  GraduationCap,
  Clock,
  Building,
  Check,
  X,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { motion, Reorder } from 'framer-motion';

// Icon mapping for employment types
const TYPE_ICONS = {
  paye_employee: Users,
  paye_part_time: Clock,
  ir35_inside: FileCheck,
  ir35_outside: Receipt,
  contractor: Briefcase,
  cis_subcontractor: HardHat,
  apprentice: GraduationCap,
  fixed_term: Calendar,
  agency: Building
};

// Stat Pill Component
const StatPill = ({ icon: Icon, label, value, color = "slate" }) => {
  const colorConfig = {
    slate: { bg: "bg-slate-50", icon: "bg-slate-100 text-slate-600", value: "text-slate-800" },
    indigo: { bg: "bg-indigo-50", icon: "bg-indigo-100 text-indigo-600", value: "text-indigo-700" },
    emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-600", value: "text-emerald-700" },
    amber: { bg: "bg-amber-50", icon: "bg-amber-100 text-amber-600", value: "text-amber-700" }
  };
  const cfg = colorConfig[color];

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${cfg.bg} border border-slate-200/50`}>
      <div className={`p-2 rounded-lg ${cfg.icon}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className={`text-xl font-bold ${cfg.value}`}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
};

// Employment Type Row Component
const EmploymentTypeRow = ({ type, memberCount, onEdit, onToggleActive, onDelete, isDragging }) => {
  const Icon = TYPE_ICONS[type.code] || Briefcase;

  return (
    <motion.div
      layout
      className={cn(
        "group relative bg-white rounded-xl border border-slate-200/80 p-4 transition-all duration-300",
        isDragging ? "shadow-lg border-slate-300 z-10" : "hover:shadow-md hover:border-slate-300",
        !type.is_active && "opacity-60"
      )}
    >
      <div className="flex items-start gap-4">
        {/* Drag Handle */}
        <div className="flex items-center justify-center w-6 h-6 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 transition-colors">
          <GripVertical className="h-4 w-4" />
        </div>

        {/* Icon */}
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${type.color}20` || '#6B728020' }}
        >
          <Icon className="h-5 w-5" style={{ color: type.color || '#6B7280' }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-slate-800">{type.name}</h3>
            {type.is_system_default && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600 border border-indigo-200">
                System
              </span>
            )}
            {!type.is_active && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                Inactive
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mb-3 line-clamp-1">{type.description}</p>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2">
            {type.has_leave_entitlement ? (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <Calendar className="h-3 w-3" />
                {type.default_annual_leave} days leave
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-50 text-slate-500 border border-slate-200">
                <X className="h-3 w-3" />
                No leave
              </span>
            )}
            {type.entitled_to_bank_holidays && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                <Check className="h-3 w-3" />
                Bank holidays
              </span>
            )}
            {type.entitled_to_sick_pay && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                <Stethoscope className="h-3 w-3" />
                Sick pay
              </span>
            )}
            {type.is_paye && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <Receipt className="h-3 w-3" />
                PAYE
              </span>
            )}
            {type.is_cis && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                <HardHat className="h-3 w-3" />
                CIS
              </span>
            )}
            {type.requires_ir35_determination && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                <Shield className="h-3 w-3" />
                IR35
              </span>
            )}
          </div>
        </div>

        {/* Member Count */}
        <div className="text-center px-3">
          <p className="text-lg font-bold text-slate-800">{memberCount}</p>
          <p className="text-xs text-slate-500">members</p>
        </div>

        {/* Actions */}
        <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleActive(type)}
            className="h-8 w-8 p-0 rounded-lg"
            title={type.is_active ? 'Deactivate' : 'Activate'}
          >
            {type.is_active ? (
              <X className="h-4 w-4 text-slate-500" />
            ) : (
              <Check className="h-4 w-4 text-emerald-500" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(type)}
            className="h-8 w-8 p-0 rounded-lg"
          >
            <Edit className="h-4 w-4 text-slate-500" />
          </Button>
          {!type.is_system_default && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(type)}
              className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Color picker presets
const COLOR_PRESETS = [
  '#10B981', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
  '#EC4899', '#F43F5E', '#F97316', '#F59E0B', '#84CC16',
  '#14B8A6', '#0EA5E9', '#6B7280', '#374151', '#1F2937'
];

export const EmploymentTypesPage = () => {
  const navigate = useNavigate();
  const { userProfile, can, loading: authLoading, permissionsLoaded } = useAuth();

  usePageTitle('Employment Types');

  const authReady = !authLoading && permissionsLoaded;

  useEffect(() => {
    if (authReady && !can('attendance.manage')) {
      toast.error('You do not have permission to manage employment types');
      navigate('/dashboard');
    }
  }, [authReady, can, navigate]);

  // State
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deletingType, setDeletingType] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    color: '#6B7280',
    has_leave_entitlement: true,
    default_annual_leave: 25,
    entitled_to_bank_holidays: true,
    entitled_to_sick_pay: true,
    entitled_to_carry_over: true,
    max_carry_over_days: 5,
    is_paye: true,
    is_cis: false,
    requires_ir35_determination: false,
    leave_requires_approval: true,
    track_working_hours: true
  });

  // Fetch employment types
  const fetchData = useCallback(async () => {
    if (!userProfile?.company_id) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch employment types
      const { data: types, error: typesError } = await supabase
        .from('employment_types')
        .select('*')
        .eq('company_id', userProfile.company_id)
        .order('display_order', { ascending: true });

      if (typesError) throw typesError;

      // Fetch member counts per type
      const { data: counts, error: countsError } = await supabase
        .from('company_members')
        .select('employment_type_id')
        .eq('company_id', userProfile.company_id);

      if (countsError) throw countsError;

      // Calculate counts
      const countMap = {};
      counts.forEach(member => {
        if (member.employment_type_id) {
          countMap[member.employment_type_id] = (countMap[member.employment_type_id] || 0) + 1;
        }
      });

      setEmploymentTypes(types || []);
      setMemberCounts(countMap);
    } catch (err) {
      console.error('Error fetching employment types:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.company_id]);

  useEffect(() => {
    if (authReady && can('attendance.manage')) {
      fetchData();
    }
  }, [authReady, can, fetchData]);

  // Stats
  const stats = useMemo(() => {
    const active = employmentTypes.filter(t => t.is_active).length;
    const withLeave = employmentTypes.filter(t => t.has_leave_entitlement && t.is_active).length;
    const paye = employmentTypes.filter(t => t.is_paye && t.is_active).length;
    const totalMembers = Object.values(memberCounts).reduce((sum, count) => sum + count, 0);

    return { active, withLeave, paye, totalMembers };
  }, [employmentTypes, memberCounts]);

  // Filtered types
  const filteredTypes = useMemo(() => {
    if (!searchQuery) return employmentTypes;
    const query = searchQuery.toLowerCase();
    return employmentTypes.filter(type =>
      type.name.toLowerCase().includes(query) ||
      type.code.toLowerCase().includes(query) ||
      type.description?.toLowerCase().includes(query)
    );
  }, [employmentTypes, searchQuery]);

  // Generate code from name
  const generateCode = useCallback((name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 30);
  }, []);

  // Open create dialog
  const openCreateDialog = useCallback(() => {
    setEditingType(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      color: '#6B7280',
      has_leave_entitlement: true,
      default_annual_leave: 25,
      entitled_to_bank_holidays: true,
      entitled_to_sick_pay: true,
      entitled_to_carry_over: true,
      max_carry_over_days: 5,
      is_paye: true,
      is_cis: false,
      requires_ir35_determination: false,
      leave_requires_approval: true,
      track_working_hours: true
    });
    setDialogOpen(true);
  }, []);

  // Open edit dialog
  const openEditDialog = useCallback((type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      code: type.code,
      description: type.description || '',
      color: type.color || '#6B7280',
      has_leave_entitlement: type.has_leave_entitlement,
      default_annual_leave: type.default_annual_leave,
      entitled_to_bank_holidays: type.entitled_to_bank_holidays,
      entitled_to_sick_pay: type.entitled_to_sick_pay,
      entitled_to_carry_over: type.entitled_to_carry_over,
      max_carry_over_days: type.max_carry_over_days,
      is_paye: type.is_paye,
      is_cis: type.is_cis,
      requires_ir35_determination: type.requires_ir35_determination,
      leave_requires_approval: type.leave_requires_approval,
      track_working_hours: type.track_working_hours
    });
    setDialogOpen(true);
  }, []);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    const code = formData.code || generateCode(formData.name);
    if (!code) {
      toast.error('Please enter a valid code');
      return;
    }

    setProcessing(true);

    try {
      if (editingType) {
        // Update existing
        const { error } = await supabase
          .from('employment_types')
          .update({
            name: formData.name,
            description: formData.description,
            color: formData.color,
            has_leave_entitlement: formData.has_leave_entitlement,
            default_annual_leave: formData.default_annual_leave,
            entitled_to_bank_holidays: formData.entitled_to_bank_holidays,
            entitled_to_sick_pay: formData.entitled_to_sick_pay,
            entitled_to_carry_over: formData.entitled_to_carry_over,
            max_carry_over_days: formData.max_carry_over_days,
            is_paye: formData.is_paye,
            is_cis: formData.is_cis,
            requires_ir35_determination: formData.requires_ir35_determination,
            leave_requires_approval: formData.leave_requires_approval,
            track_working_hours: formData.track_working_hours
          })
          .eq('id', editingType.id);

        if (error) throw error;
        toast.success('Employment type updated');
      } else {
        // Create new
        const maxOrder = Math.max(...employmentTypes.map(t => t.display_order), 0);
        const { error } = await supabase
          .from('employment_types')
          .insert({
            company_id: userProfile.company_id,
            name: formData.name,
            code: code,
            description: formData.description,
            color: formData.color,
            has_leave_entitlement: formData.has_leave_entitlement,
            default_annual_leave: formData.default_annual_leave,
            entitled_to_bank_holidays: formData.entitled_to_bank_holidays,
            entitled_to_sick_pay: formData.entitled_to_sick_pay,
            entitled_to_carry_over: formData.entitled_to_carry_over,
            max_carry_over_days: formData.max_carry_over_days,
            is_paye: formData.is_paye,
            is_cis: formData.is_cis,
            requires_ir35_determination: formData.requires_ir35_determination,
            leave_requires_approval: formData.leave_requires_approval,
            track_working_hours: formData.track_working_hours,
            is_system_default: false,
            display_order: maxOrder + 1
          });

        if (error) throw error;
        toast.success('Employment type created');
      }

      setDialogOpen(false);
      setEditingType(null);
      fetchData();
    } catch (err) {
      console.error('Error saving employment type:', err);
      toast.error(err.message || 'Failed to save');
    } finally {
      setProcessing(false);
    }
  }, [formData, editingType, employmentTypes, userProfile?.company_id, generateCode, fetchData]);

  // Toggle active status
  const handleToggleActive = useCallback(async (type) => {
    try {
      const { error } = await supabase
        .from('employment_types')
        .update({ is_active: !type.is_active })
        .eq('id', type.id);

      if (error) throw error;
      toast.success(type.is_active ? 'Employment type deactivated' : 'Employment type activated');
      fetchData();
    } catch (err) {
      console.error('Error toggling status:', err);
      toast.error('Failed to update status');
    }
  }, [fetchData]);

  // Delete handler
  const handleDelete = useCallback(async () => {
    if (!deletingType) return;

    const count = memberCounts[deletingType.id] || 0;
    if (count > 0) {
      toast.error(`Cannot delete: ${count} members are assigned to this type`);
      setDeleteDialogOpen(false);
      return;
    }

    setProcessing(true);

    try {
      const { error } = await supabase
        .from('employment_types')
        .delete()
        .eq('id', deletingType.id);

      if (error) throw error;
      toast.success('Employment type deleted');
      setDeleteDialogOpen(false);
      setDeletingType(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting employment type:', err);
      toast.error(err.message || 'Failed to delete');
    } finally {
      setProcessing(false);
    }
  }, [deletingType, memberCounts, fetchData]);

  // Reorder handler
  const handleReorder = useCallback(async (newOrder) => {
    setEmploymentTypes(newOrder);

    // Update display_order in database
    try {
      const updates = newOrder.map((type, index) => ({
        id: type.id,
        display_order: index + 1
      }));

      for (const update of updates) {
        await supabase
          .from('employment_types')
          .update({ display_order: update.display_order })
          .eq('id', update.id);
      }
    } catch (err) {
      console.error('Error updating order:', err);
      toast.error('Failed to update order');
      fetchData(); // Revert on error
    }
  }, [fetchData]);

  // Loading state
  if (!authReady) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading...</p>
        </motion.div>
      </div>
    );
  }

  if (!can('attendance.manage')) {
    return null;
  }

  if (loading && !employmentTypes.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading employment types...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-rose-50 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-rose-500" />
          </div>
          <p className="text-slate-800 font-semibold">Failed to load employment types</p>
          <p className="text-slate-500 text-sm mt-1">{error}</p>
          <Button onClick={fetchData} className="mt-4 rounded-full">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Employment Types</h1>
            <p className="text-slate-500 mt-1">Configure employment types and their entitlement rules</p>
          </div>

          <Button onClick={openCreateDialog} className="h-10 bg-slate-800 hover:bg-slate-700 rounded-full px-5">
            <Plus className="h-4 w-4 mr-2" />
            Add Type
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatPill icon={Briefcase} label="Active Types" value={stats.active} color="slate" />
          <StatPill icon={Calendar} label="With Leave" value={stats.withLeave} color="emerald" />
          <StatPill icon={Receipt} label="PAYE Types" value={stats.paye} color="amber" />
          <StatPill icon={Users} label="Total Members" value={stats.totalMembers} color="indigo" />
        </div>
      </motion.div>

      {/* Types List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        {/* Search */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search employment types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 rounded-full border-slate-200"
            />
          </div>
        </div>

        {/* List Header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            {filteredTypes.length} employment type{filteredTypes.length !== 1 ? 's' : ''}
            {loading && <Loader2 className="inline h-4 w-4 ml-2 animate-spin" />}
          </p>
          <p className="text-xs text-slate-400">Drag to reorder</p>
        </div>

        {/* Types */}
        {filteredTypes.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-slate-50 flex items-center justify-center">
              <Briefcase className="h-7 w-7 text-slate-300" />
            </div>
            <p className="text-slate-500 font-medium">No employment types found</p>
            <p className="text-slate-400 text-sm mt-1">Create your first employment type to get started</p>
          </div>
        ) : (
          <Reorder.Group
            axis="y"
            values={filteredTypes}
            onReorder={handleReorder}
            className="space-y-2"
          >
            {filteredTypes.map((type) => (
              <Reorder.Item key={type.id} value={type}>
                <EmploymentTypeRow
                  type={type}
                  memberCount={memberCounts[type.id] || 0}
                  onEdit={openEditDialog}
                  onToggleActive={handleToggleActive}
                  onDelete={(t) => { setDeletingType(t); setDeleteDialogOpen(true); }}
                />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}
      </motion.div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Employment Type' : 'Add Employment Type'}</DialogTitle>
            <DialogDescription>
              {editingType ? 'Update the employment type details and rules.' : 'Create a new employment type with entitlement rules.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      code: editingType ? formData.code : generateCode(e.target.value)
                    });
                  }}
                  placeholder="e.g. PAYE Employee"
                  className="rounded-lg"
                />
              </div>

              {!editingType && (
                <div className="space-y-2">
                  <Label>Code</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Auto-generated from name"
                    className="rounded-lg font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">Unique identifier (auto-generated)</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this employment type"
                  className="rounded-lg resize-none"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={cn(
                        "w-8 h-8 rounded-lg border-2 transition-all",
                        formData.color === color ? "border-slate-800 scale-110" : "border-transparent hover:scale-105"
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Leave Entitlements */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Leave Entitlements
              </h3>

              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <Label>Entitled to Leave</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Can request and accrue leave</p>
                </div>
                <Switch
                  checked={formData.has_leave_entitlement}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_leave_entitlement: checked })}
                />
              </div>

              {formData.has_leave_entitlement && (
                <>
                  <div className="space-y-2">
                    <Label>Default Annual Leave Days</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.default_annual_leave}
                      onChange={(e) => setFormData({ ...formData, default_annual_leave: parseFloat(e.target.value) || 0 })}
                      className="rounded-lg"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div>
                      <Label>Bank Holiday Entitlement</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Entitled to paid bank holidays</p>
                    </div>
                    <Switch
                      checked={formData.entitled_to_bank_holidays}
                      onCheckedChange={(checked) => setFormData({ ...formData, entitled_to_bank_holidays: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div>
                      <Label>Sick Pay Entitlement</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Entitled to statutory sick pay</p>
                    </div>
                    <Switch
                      checked={formData.entitled_to_sick_pay}
                      onCheckedChange={(checked) => setFormData({ ...formData, entitled_to_sick_pay: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div>
                      <Label>Carry Over Allowed</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Can carry over unused leave</p>
                    </div>
                    <Switch
                      checked={formData.entitled_to_carry_over}
                      onCheckedChange={(checked) => setFormData({ ...formData, entitled_to_carry_over: checked })}
                    />
                  </div>

                  {formData.entitled_to_carry_over && (
                    <div className="space-y-2">
                      <Label>Max Carry Over Days</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.max_carry_over_days}
                        onChange={(e) => setFormData({ ...formData, max_carry_over_days: parseFloat(e.target.value) || 0 })}
                        className="rounded-lg"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div>
                      <Label>Requires Approval</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Leave requests need manager approval</p>
                    </div>
                    <Switch
                      checked={formData.leave_requires_approval}
                      onCheckedChange={(checked) => setFormData({ ...formData, leave_requires_approval: checked })}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Tax & Compliance */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                Tax & Compliance
              </h3>

              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <Label>PAYE</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Tax deducted via Pay As You Earn</p>
                </div>
                <Switch
                  checked={formData.is_paye}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_paye: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <Label>CIS (Construction Industry Scheme)</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Subject to CIS deductions</p>
                </div>
                <Switch
                  checked={formData.is_cis}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_cis: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <Label>Requires IR35 Determination</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Needs IR35 status assessment</p>
                </div>
                <Switch
                  checked={formData.requires_ir35_determination}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_ir35_determination: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <Label>Track Working Hours</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Record hours worked for this type</p>
                </div>
                <Switch
                  checked={formData.track_working_hours}
                  onCheckedChange={(checked) => setFormData({ ...formData, track_working_hours: checked })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={processing} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={processing || !formData.name.trim()} className="rounded-full bg-slate-800 hover:bg-slate-700">
              {processing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editingType ? 'Updating...' : 'Creating...'}</>
              ) : (
                editingType ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Employment Type?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {deletingType && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200">
              <p className="font-medium text-slate-800">{deletingType.name}</p>
              <p className="text-sm text-slate-500 mt-1">{deletingType.description}</p>
              {(memberCounts[deletingType.id] || 0) > 0 && (
                <p className="text-sm text-rose-600 mt-2 font-medium">
                  {memberCounts[deletingType.id]} member(s) are assigned to this type
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={processing} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={processing || (deletingType && (memberCounts[deletingType.id] || 0) > 0)}
              className="rounded-full bg-rose-600 hover:bg-rose-700"
            >
              {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : <><Trash2 className="h-4 w-4 mr-2" />Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
